import type { Nullable } from '@zen/utils/typescript';

const CELL_HIGHLIGHTED_CLASS = '!bg-grey-lightest';
const MERGE_COLUMN_CELL_CLASS = 'merge-column-cell';
const TABLE_SELECTOR = 'table[data-component="table"]';
const TABLE_ROW_CLASS = 'zen-table-row';

const getCellRowSpan = (element: Nullable<HTMLTableCellElement>): number => {
  const rowSpanAttribute = element?.getAttribute('rowspan');

  return rowSpanAttribute ? parseInt(rowSpanAttribute, 10) : 1;
};

const isMergedCell = (element: Nullable<HTMLTableCellElement>): boolean => {
  return element?.classList.contains(MERGE_COLUMN_CELL_CLASS) ?? false;
};

const getOwnRows = (table: Element): HTMLTableRowElement[] => {
  const tableElement: Nullable<HTMLTableElement> = table.querySelector<HTMLTableElement>(TABLE_SELECTOR);

  if (!tableElement) {
    return [];
  }

  return Array.from(tableElement.tBodies).flatMap((body: HTMLTableSectionElement) =>
    Array.from(body.rows).filter((row: HTMLTableRowElement) => row.classList.contains(TABLE_ROW_CLASS))
  );
};

const getOwnCells = (row: HTMLTableRowElement): HTMLTableCellElement[] => Array.from(row.cells);

/**
 * Highlights cells in the specified row range.
 * Regular cells are always highlighted.
 * Merged cells are only highlighted when includeMergedCells is true
 * and their span doesn't extend beyond endRow.
 */
const highlightCellsInRows = (
  rows: HTMLTableRowElement[],
  startRow: number,
  endRow: number,
  includeMergedCells: boolean
): void => {
  for (let index = startRow; index <= endRow; index++) {
    const row = rows[index];

    if (!row) {
      continue;
    }

    getOwnCells(row).forEach((cell: HTMLTableCellElement) => {
      const isMerged = cell.classList.contains(MERGE_COLUMN_CELL_CLASS);

      if (isMerged && (!includeMergedCells || index + getCellRowSpan(cell) - 1 > endRow)) {
        return;
      }

      cell.classList.add(CELL_HIGHLIGHTED_CLASS);
    });
  }
};

const removeAllHighlights = (tableId: string): void => {
  const table: Nullable<Element> = document.querySelector(`div.${tableId}`);

  if (!table) {
    return;
  }

  getOwnRows(table).forEach((row: HTMLTableRowElement) => {
    getOwnCells(row).forEach((cell: HTMLTableCellElement) => cell.classList.remove(CELL_HIGHLIGHTED_CLASS));
  });
};

export interface CellHoverProps {
  element: Nullable<HTMLTableCellElement>;
  tableId: string;
}

/**
 * Highlights cells when hovering:
 * - Regular cell: highlights only regular cells in that row
 * - Merged cell: highlights regular cells + fitting merged cells across its row span
 *
 * The hovered row's position is read from the DOM rather than the row's data index, because
 * grouping reorders rows and collapsed groups remove them, so the two can differ.
 */
export const onCellMouseEnter = ({ element, tableId }: CellHoverProps): void => {
  const table: Nullable<Element> = document.querySelector(`div.${tableId}`);
  const hoveredRow: Nullable<HTMLTableRowElement> = element?.closest('tr') ?? null;

  if (!table || !hoveredRow) {
    return;
  }

  const rows: HTMLTableRowElement[] = getOwnRows(table);
  const startRow: number = rows.indexOf(hoveredRow);

  if (startRow === -1) {
    return;
  }

  const endRow: number = startRow + getCellRowSpan(element) - 1;

  highlightCellsInRows(rows, startRow, endRow, isMergedCell(element));
};

export const onCellMouseLeave = ({ tableId }: Pick<CellHoverProps, 'tableId'>): void => {
  removeAllHighlights(tableId);
};
